import type { SlotRosterDetailsInput } from '@sportninja/common/services/teamBalance'
import type {
  BalanceGridRow,
  BalanceTableRow,
  RosterDetailChange,
} from '../types/balanceGrid'
import { isDataRow } from './balanceGridActions'
import { slotPlayerNumber, slotPositionId } from './balanceFinder'
import { balanceText } from './balanceI18n'

/** The two double-click columns, by column id. */
export type RosterDetailField = 'position' | 'player_number'

/**
 * Inon, Oct 2 — what committing a double-clicked Position or Number means (the
 * columns' `valueSetter`): the row it was typed on and the one field that changed, or
 * null when there is nothing to send.
 *
 *   - `undefined` is an editor that handed back nothing: Number's refuses a value its
 *     min/max/step rule out (1.5, 1000, -1), which the browser flags as invalid.
 *   - Position arrives as the picked position's id. Picking the one already shown
 *     closes the list and sends nothing, and it can never be cleared.
 *   - Number arrives as a number, or null when the box was emptied — a clear, sent as
 *     `null`. The same number again sends nothing.
 */
export const rosterDetailCommit = (
  row: BalanceGridRow | undefined,
  field: RosterDetailField,
  raw: unknown
): { row: BalanceTableRow; change: RosterDetailChange } | null => {
  if (raw === undefined || !isDataRow(row)) {
    return null
  }
  const target: BalanceTableRow = {
    key: row.key,
    slot: row.slot,
    registration: row.registration,
  }

  if (field === 'position') {
    const positionId = raw === null || raw === '' ? NaN : Number(raw)
    if (
      !Number.isInteger(positionId) ||
      String(positionId) === slotPositionId(row.slot)
    ) {
      return null
    }

    return { row: target, change: { player_type_id: positionId } }
  }

  const number = raw === null || raw === '' ? null : Number(raw)
  if (
    (number !== null && !Number.isFinite(number)) ||
    number === slotPlayerNumber(row.slot)
  ) {
    return null
  }

  return { row: target, change: { player_number: number } }
}

/**
 * What a save expects of the row, as the rep saw it: the occupant's registration, or —
 * on a hold nobody has claimed — who it is held for. The server refuses the save (409)
 * when either has changed, so a number meant for one person never lands on another.
 */
export const rosterDetailExpectations = (
  row: BalanceTableRow
): Pick<
  SlotRosterDetailsInput,
  'expected_player_registration_id' | 'expected_invitee'
> => {
  const { slot } = row
  if (slot.player_registration_id !== null || !slot.invitee) {
    return { expected_player_registration_id: slot.player_registration_id }
  }

  return {
    expected_player_registration_id: null,
    expected_invitee: {
      email: slot.invitee.email ?? '',
      name_first: slot.invitee.name_first ?? '',
      name_last: slot.invitee.name_last ?? '',
    },
  }
}

const FAILED_KEYS: Record<RosterDetailField, string> = {
  position: 'common:balanceRosterPositionFailed',
  player_number: 'common:balanceRosterNumberFailed',
}

/** The server's field for each column. */
const SERVER_FIELDS: Record<RosterDetailField, string> = {
  position: 'player_type_id',
  player_number: 'player_number',
}

/**
 * What a refused save says: the server's message for the field, else for the slot
 * (not on the roster yet, someone else's line), else its own message (a 409 says the
 * row changed), else ours — the order the Share's refusals use.
 */
export const rosterDetailErrorMessage = (
  error: unknown,
  field: RosterDetailField
): string => {
  const fields = (error as { invalid_fields?: Record<string, string> } | null)
    ?.invalid_fields

  return (
    fields?.[SERVER_FIELDS[field]] ||
    fields?.slot ||
    (error as Error | null)?.message ||
    balanceText(FAILED_KEYS[field])
  )
}
